import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Stack, Text, Button, Overlay, PasswordInput, LoadingBalls } from '@moda/om';
import { useForm } from '@moda/portal-stanchions';
import { USER_API_URL } from '../../constants';
import { identifyUser } from '../../lib/identifyUser';
import { handleAuthSuccess, AuthResponseSuccess, AuthPayloadSuccess } from '../../lib/authenticate';
import { hrefFor } from '../../routers';
import { usePasswordValidation } from '../../hooks/usePasswordValidation';
import { useUser } from '../../hooks/useUser';
import { useNotifications } from '../../hooks/useNotifications';
import { useCookies } from '../../hooks/useCookies';

import './ResetPassword.scss';

const resetPassword = (resetPasswordToken: string, password: string) =>
  fetch(`${USER_API_URL}/v1/reset_password`, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/vnd.api+json' },
    redirect: 'follow',
    referrer: 'no-referrer',
    body: JSON.stringify({
      data: {
        type: 'reset_password',
        attributes: { reset_password_token: resetPasswordToken, password }
      }
    })
  })
    .then(res => res.json())
    .then(data => {
      if (data.meta && data.data) {
        return handleAuthSuccess(data as AuthResponseSuccess);
      }
      if (data.errors?.[0]) {
        throw new Error(data.errors[0].detail);
      }

      throw new Error('Something went wrong, please retry later.');
    });

export const ResetPassword: React.FC = () => {
  const history = useHistory();
  const {
    cookies: { passwordResetToken }
  } = useCookies();

  const { setUserData } = useUser();
  const { addNotification } = useNotifications();

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSuccess = useCallback(
    async (user: AuthPayloadSuccess) => {
      setLoading(false);
      identifyUser(user);
      await setUserData({ remember: true, isLoggedIn: true, ...user });

      addNotification({
        type: 'success',
        message: 'Your password was changed successfully. You are now signed in.'
      });

      history.replace(hrefFor.HomePage());
    },
    [addNotification, history, setUserData]
  );

  const handleError = useCallback((error: Error) => {
    setLoading(false);
    setError(error.message);
  }, []);

  const onSubmit = useCallback(
    ({ password }: { password: string }) => {
      setLoading(true);
      resetPassword(String(passwordResetToken), password).then(handleSuccess).catch(handleError);
    },
    [passwordResetToken, handleSuccess, handleError]
  );

  const { passwordValidation } = usePasswordValidation();
  const { errors: fieldErrors, handleSubmit } = useForm({
    onSubmit,
    validations: { password: passwordValidation }
  });

  return (
    <form className="ResetPassword" onSubmit={handleSubmit}>
      <Stack className="ResetPassword__content" space={3}>
        <Text treatment="h4" family="serif">
          Change Password
        </Text>
        <Stack space={2}>
          <Text treatment="body2">Please enter a new password below to change your password.</Text>
          <PasswordInput
            name="password"
            placeholder="Password"
            error={fieldErrors.password?.message ?? error}
          />
        </Stack>
        <Button type="submit">CHANGE PASSWORD</Button>
      </Stack>
      <Overlay show={loading}>
        <LoadingBalls />

        <Text treatment="eyebrow">Changing password</Text>
      </Overlay>
    </form>
  );
};
